import { useState, useMemo, useEffect } from "react";
import { Modal, Message } from "rsuite";
import { Button } from "../";
import TransactionDetails from "./TransactionDetails";
import logger from "../../utils/logger";
import "./ChildSearchModal.css";
import ChildSearchService from "../../services/childSearch.service";
import config from "../../config";
import IgnoreTransactionModal from "./components/IgnoreTransactionModal";
import ChangeExportTypeModal from "./components/ChangeExportTypeModal";
import DefaultLinkModal from "./components/DefaultLinkModal";
import SearchForm from "./components/SearchForm";
import SearchResults from "./components/SearchResults";
export default function ChildSearchModal({ open, onClose, onChildSelect, fileTypeId, transaction, ignoreReasons, onIgnoreTransaction, fileTypes, onChangeExportType, initialSelectedIgnoreReason = null, forceSelectedIgnoreReason = null, hideAlternativeActions = false, showNotInCmsOption = false, onNotInCmsSelect, centreId, selectTransactions = false, showLink = true }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [maxEntries, setMaxEntries] = useState(0);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [childDetails, setChildDetails] = useState({});
  // Transaction field checkboxes - default to checked
  const [includeParticulars, setIncludeParticulars] = useState(true);
  const [includeCode, setIncludeCode] = useState(true);
  const [includeReference, setIncludeReference] = useState(true);
  // Whether to do an upsert match when linking the child. Default true.
  const [includeDoUpsertMatch, setIncludeDoUpsertMatch] = useState(true);

  // Ensure checkbox defaults reflect the incoming transaction prop when it changes.
  useEffect(() => {
    setIncludeParticulars(Boolean(transaction?.particulars));
    setIncludeCode(Boolean(transaction?.code));
    setIncludeReference(Boolean(transaction?.reference));
    // default DoUpsertMatch to true for each new transaction
    setIncludeDoUpsertMatch(true);
  }, [transaction]);

  // Default link confirm modal
  const [isDefaultModalOpen, setIsDefaultModalOpen] = useState(false);
  // Ignore transaction modal
  const [isIgnoreModalOpen, setIsIgnoreModalOpen] = useState(false);
  const [selectedIgnoreReason, setSelectedIgnoreReason] = useState(initialSelectedIgnoreReason);
  
  // Change export type modal
  const [isChangeExportTypeModalOpen, setIsChangeExportTypeModalOpen] = useState(false);
  const [selectedFileType, setSelectedFileType] = useState(null);

  // Allow tests or parent to forcefully set the selected ignore reason
  useEffect(() => {
    if (forceSelectedIgnoreReason) {
      setSelectedIgnoreReason(forceSelectedIgnoreReason);
    }
  }, [forceSelectedIgnoreReason]);

  const handleSearch = async (copySearch = "") => {
    const searchValue = typeof copySearch === "string" ? copySearch.trim() : "";
    if (!searchValue && !searchTerm?.trim()) {
      setError("Please enter a search term");
      return;
    }

    setLoading(true);
    setError("");
    setHasSearched(true);

    try {
      const result = await ChildSearchService.searchChildren(searchValue || searchTerm?.trim(), maxEntries, centreId);
      const childrenData = Array.isArray(result) ? result : [];
      setChildren(childrenData);
    } catch (err) {
      logger.error("Error searching children:", err);
      setError("Failed to search children. Please try again.");
      setChildren([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDirectChildId = (childId) => {
    if (!childId.trim()) {
      setError("Please enter a Child ID");
      return;
    }

    // Call the placeholder function with the direct child ID
    return handleChildSelect({
      childId: childId.trim(),
      DoUpsertMatch: includeDoUpsertMatch,
      // only include the other checkbox flags when DoUpsertMatch is true
      ...(includeDoUpsertMatch ? { includeParticulars, includeCode, includeReference } : {})
    });
  };

  const handleChildSelect = (childData) => {
    // If selectionOnly mode, just return the child data without creating matches
    if (onChildSelect) {
      const returnChild = {
        ...childData,
        DoUpsertMatch: includeDoUpsertMatch,
        // only include the other checkbox flags when DoUpsertMatch is true
        ...(includeDoUpsertMatch ? { includeParticulars, includeCode, includeReference } : {})
      };
      // return the result of onChildSelect so callers can await it
      return onChildSelect(returnChild);
    }
    return;
  };

  const handleClose = () => {
    setSearchTerm("");
    setMaxEntries(0);
    setChildren([]);
    setError("");
    setHasSearched(false);
    setIsIgnoreModalOpen(false);
    setSelectedIgnoreReason(null);
    setIsChangeExportTypeModalOpen(false);
  setSelectedFileType(null);

    // Reset transaction field checkboxes to default (checked)
    setIncludeParticulars(true);
    setIncludeCode(true);
    setIncludeReference(true);
    setIncludeDoUpsertMatch(true);

    onClose();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearch();
    }
  };

  const handleDefaultChildLink = async () => {
    const defaultId = config?.defaultChildId;
    // Set the direct child id to the default value and close the modal
    try {
      await handleDirectChildId(defaultId);
      setIsDefaultModalOpen(false);
    } catch (err) {
      logger.error("Error setting default child:", err);
      setError("Failed to set default child. Please try again.");
    }
  };

  const handleIgnoreTransaction = async () => {
    if (!selectedIgnoreReason?.value || !transaction?.id) {
      setError("Please select an ignore reason");
      return;
    }

    try {
      if (onIgnoreTransaction) {
        await onIgnoreTransaction(
          transaction.id,
          selectedIgnoreReason.value,
        );
      }
      setIsIgnoreModalOpen(false);
      setSelectedIgnoreReason(null);
      onClose();
    } catch (err) {
      logger.error("Error ignoring transaction:", err);
      setError("Failed to ignore transaction. Please try again.");
    }
  };

  const handleChangeExportType = async () => {
    if (!selectedFileType?.value || !transaction?.id) {
      setError("Please select a file type");
      return;
    }

    try {
      if (onChangeExportType) {
        await onChangeExportType(
          transaction.id,
          selectedFileType.value,
        );
      }
      setIsChangeExportTypeModalOpen(false);
      setSelectedFileType(null);
    } catch (err) {
      logger.error("Error changing export type:", err);
      setError("Failed to change export type. Please try again.");
    }
  };

  useEffect(() => {
    if (transaction?.childId) {
      // Fetch child details when transaction.childId is set
      ChildSearchService.getChildDetails(transaction.childId)
        .then((details) => {
          // Handle successful retrieval of child details
          setChildDetails(details);
        })
        .catch((error) => {
          // Handle errors
          logger.error("Error fetching child details:", error);
        });
    } else {
      setChildDetails({});
    }
  }, [transaction?.childId]);

  useEffect(() => {
    if (!open) {
      handleClose();
    }
  }, [open]);

  return (
    <>
      <Modal backdrop="static" keyboard={true} open={open} onClose={handleClose} size="calc(100% - 150px)" className="child-search-modal">
        <Modal.Header>
          <Modal.Title>Match Transaction to Child</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="child-search-content">
            {/* Transaction Details Section */}
            <TransactionDetails
              fileTypeId={fileTypeId}
              fileTypes={fileTypes}
              transaction={transaction}
              childDetails={childDetails}
              includeParticulars={includeParticulars}
              setIncludeParticulars={setIncludeParticulars}
              includeCode={includeCode}
              setIncludeCode={setIncludeCode}
              includeReference={includeReference}
              setIncludeReference={setIncludeReference}
              includeDoUpsertMatch={includeDoUpsertMatch}
              setIncludeDoUpsertMatch={setIncludeDoUpsertMatch}
              onCopy={(value) => {
                setSearchTerm(value);
                handleSearch(value);
              }}
            />

            <SearchForm
              searchTerm={searchTerm}
              onSearchTermChange={setSearchTerm}
              onKeyDown={handleKeyDown}
              onSearch={handleSearch}
              loading={loading}
              onOpenDefaultModal={() => setIsDefaultModalOpen(true)}
              onOpenIgnoreModal={() => setIsIgnoreModalOpen(true)}
              onOpenChangeExportTypeModal={() => setIsChangeExportTypeModalOpen(true)}
              ignoreReasons={ignoreReasons}
              fileTypes={fileTypes}
              hideAlternativeActions={hideAlternativeActions}
              showNotInCmsOption={showNotInCmsOption}
              onNotInCmsSelect={onNotInCmsSelect}
            />

            {/* Error Message */}
            {error && (
              <Message showIcon type="error" className="error-message">
                {error}
              </Message>
            )}

            {/* Search Results */}
            <SearchResults 
              children={children}
              onSelectChild={handleChildSelect}
              loading={loading}
              hasSearched={hasSearched}
              selectTransactions={selectTransactions}
              showLink={showLink}
            />
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button text="Close" appearance="subtle" onClick={handleClose} data-testid="close-modal-btn" color="red" />
        </Modal.Footer>
      </Modal>

      <DefaultLinkModal loading={loading} open={isDefaultModalOpen} onClose={() => setIsDefaultModalOpen(false)} onConfirm={handleDefaultChildLink} />

      <IgnoreTransactionModal
        open={isIgnoreModalOpen}
        onClose={() => {
          setIsIgnoreModalOpen(false);
          setSelectedIgnoreReason(null);
        }}
        ignoreReasons={ignoreReasons}
        selectedIgnoreReason={selectedIgnoreReason}
        onReasonChange={setSelectedIgnoreReason}
        onConfirm={handleIgnoreTransaction}
      />

      <ChangeExportTypeModal
        open={isChangeExportTypeModalOpen}
        onClose={() => {
          setIsChangeExportTypeModalOpen(false);
          setSelectedFileType(null);
        }}
        fileTypes={fileTypes}
        selectedFileType={selectedFileType}
        onFileTypeChange={setSelectedFileType}
        onConfirm={handleChangeExportType}
      />
    </>
  );
}
